import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import { Button } from '@nearst/ui';
import { ArrowRightIcon, ChevronDownIcon, CheckIcon } from '@heroicons/react/20/solid';

import Skeleton from '@components/Skeleton';
import { useShop } from '@services/ShopProvider';
import { useEnabledChannels, useChannelDefinitions } from '@services/channels';
import { getFeedQualityStats, getOrgFeedQualityStats } from '@services/stock';

import { OverviewCard } from '../../components/OverviewCard';
import { EmptyState } from '../../components/EmptyState';
import { CARD_STATES, useCardEmptyState } from '../../hooks/useCardEmptyState';

import styles from './ReachCard.module.scss';

/**
 * "Reach" card — products visible online per channel.
 *
 * Filterable across the org's enabled channels. Local Listings (`google`) and
 * Local Ads (`local-ads`) appear as separate filter options even though both
 * are backed by the same feed-quality dataset (issuesChannelId === 'google'
 * for both); the design treats them as distinct journey surfaces.
 *
 * Headline + distribution are scoped to in-stock products only (active +
 * errored); out-of-stock / inactive items are excluded. CTA points to the
 * selected channel's manage page; chain view has no org-level channel page yet,
 * so it falls back to the first shop's per-shop manage page.
 */

const SEGMENT_KEYS = [
	{ key: 'active', label: 'Active in-stock products', tone: 'success', description: 'In-stock items visible on this channel' },
	{
		key: 'errors',
		label: 'In-stock products with errors',
		tone: 'danger',
		description: 'In-stock items this channel won’t show until their product data is fixed'
	}
];

// Channels that have per-channel severity definitions in inventory-issues.json.
// Anything not in this set falls back to 'nearst' (the general data-quality
// baseline) — keeps Storefront / Meta Commerce / Product Locator etc. from
// returning empty / nonsense numbers in feed-quality-stats.
const CHANNELS_WITH_SEVERITY = new Set(['google', 'local-checkout', 'product-reservations', 'uber-eats', 'just-eat', 'deliveroo']);

const resolveIssuesChannel = (def, channelId) => {
	const override = def?.issuesChannelId;
	if (override && override !== channelId) return override;
	return CHANNELS_WITH_SEVERITY.has(channelId) ? channelId : 'nearst';
};

export const ReachCard = () => {
	const { data: shops, shop } = useShop();
	const enabledChannelsRaw = useEnabledChannels();
	const enabledChannels = enabledChannelsRaw ?? [];
	const { data: channelDefinitions } = useChannelDefinitions(undefined, { suspense: false });

	const filterOptions = useMemo(() => {
		if (!channelDefinitions) return [];
		return enabledChannels
			.map((c) => {
				const def = channelDefinitions[c.id];
				if (!def || def.comingSoon) return null;
				return {
					id: c.id,
					label: def.title ?? def.label ?? c.id,
					iconUrl: def.iconUrl ?? null,
					issuesChannelId: resolveIssuesChannel(def, c.id)
				};
			})
			.filter(Boolean);
	}, [enabledChannels, channelDefinitions]);

	const [selectedId, setSelectedId] = useState(null);
	const selected = filterOptions.find((o) => o.id === selectedId) ?? filterOptions[0];

	const scopeShopIds = shop?.id ? [shop.id] : (shops ?? []).map((s) => s.id);
	const isChainView = !shop?.id;
	const swrKey =
		selected && scopeShopIds.length ? ['feed-quality-stats', selected.issuesChannelId, isChainView ? 'org' : scopeShopIds[0]] : null;
	const {
		data: stats,
		error: statsError,
		isLoading: statsLoading
	} = useSWR(swrKey, () => {
		// Chain view: one call hits the aggregate endpoint and gets back org-wide
		// totals. Single-store: one call hits the per-shop endpoint. Either way
		// it's a single HTTP request — previously the chain branch fanned out N
		// requests and summed client-side.
		//
		// Errors deliberately propagate to SWR rather than being swallowed into
		// zeros — a failed fetch used to render as "No data for this period",
		// which hid a real outage (the MPD org-wide 400) behind an empty state.
		if (isChainView) {
			return getOrgFeedQualityStats(selected.issuesChannelId);
		}
		return getFeedQualityStats(scopeShopIds[0], selected.issuesChannelId);
	});

	// Treat the card as loading while ANY upstream dep is in flight:
	// - channelDefinitions (the JSON of channel metadata)
	// - useEnabledChannels returns `undefined` while its SWR fetch is in
	//   flight; without this gate we'd render NO_TRACTION ("no data") while
	//   the channel list is still arriving
	// - the stats fetch itself
	const isLoading = !channelDefinitions || enabledChannelsRaw === undefined || (swrKey && !stats && statsLoading);
	const allEmpty = !stats || stats.totalItems === 0;
	// Headline + status breakdown are scoped to in-stock products only (active +
	// errored). Out-of-stock / inactive items are deliberately excluded — the
	// card answers "how much of my live stock is visible online", not "how many
	// SKUs exist". Percentages below are relative to this in-stock total.
	const inStockTotal = stats ? stats.activeItems + countFor('errors', stats) : 0;
	const emptyState = useCardEmptyState({
		cardId: 'reach',
		// PENDING disabled — see comment in PerformanceCard. Empty data falls
		// through to NO_TRACTION instead of "Data is on its way".
		minDaysForData: 0,
		dataIsEmpty: allEmpty
	});

	// No org-level channel management page exists yet, so chain view falls back
	// to the first shop's per-shop channel manage page (same as single-shop view).
	const scopeShopId = shop?.id ?? shops?.[0]?.id;
	const manageHref = selected && scopeShopId ? `/channels/${scopeShopId}/manage/${selected.id}` : '/channels';
	const ctaLabel = selected ? `Manage ${selected.label}` : 'Manage channels';

	return (
		<OverviewCard>
			<div className={styles.headerRow}>
				<h2 className={styles.title}>Reach</h2>
			</div>
			<p className={styles.subtitle}>In-store products showing online</p>
			{filterOptions.length > 1 && selected && (
				<div className={styles.filterRow}>
					<ChannelDropdown options={filterOptions} selectedId={selected.id} onSelect={setSelectedId} />
				</div>
			)}

			{isLoading ? (
				<ReachCardSkeleton />
			) : statsError ? (
				<EmptyState state={CARD_STATES.ERROR} copy="Refresh the page to try again, or check back shortly." />
			) : emptyState.state !== CARD_STATES.DATA ? (
				<EmptyState state={emptyState.state} copy={emptyState.copy} ctaLabel={emptyState.ctaLabel} ctaHref={emptyState.ctaHref} />
			) : (
				<>
					<div className={styles.metricBlock}>
						<div className={styles.figure}>{inStockTotal.toLocaleString()}</div>
						<div className={styles.figureLabel}>In-stock products</div>
					</div>

					<div className={styles.statusBlock}>
						<h3 className={styles.subheading}>Product status</h3>

						<div className={styles.statusBar} aria-hidden="true">
							{SEGMENT_KEYS.map((s) => {
								const count = countFor(s.key, stats);
								const pct = inStockTotal ? (count / inStockTotal) * 100 : 0;
								if (!count) return null;
								return <span key={s.key} className={styles[`segment_${s.tone}`]} style={{ width: `${pct}%` }} />;
							})}
						</div>

						<ul className={styles.statusList}>
							{SEGMENT_KEYS.map((s) => {
								const count = countFor(s.key, stats);
								const pct = inStockTotal ? Math.round((count / inStockTotal) * 100) : 0;
								return (
									<li key={s.key} className={styles.statusItem}>
										<span className={`${styles.dot} ${styles[`dot_${s.tone}`]}`} aria-hidden="true" />
										<span className={styles.statusLabel}>{s.label}</span>
										<span className={styles.statusValue}>{count.toLocaleString()}</span>
										<span className={styles.statusPct}>{pct || '<1'}%</span>
									</li>
								);
							})}
						</ul>
					</div>
				</>
			)}

			<div className={styles.footer}>
				<Button as={Link} to={manageHref} className={styles.cta}>
					<span>{ctaLabel}</span>
					<ArrowRightIcon className={styles.arrow} aria-hidden="true" />
				</Button>
			</div>
		</OverviewCard>
	);
};

function countFor(key, stats) {
	if (key === 'active') return stats.activeItems;
	if (key === 'errors') return Math.max(0, stats.totalItems - stats.activeItems - stats.inactiveItems);
	return stats.inactiveItems;
}

const ReachCardSkeleton = () => (
	<>
		<div className={styles.metricBlock}>
			<Skeleton width={140} height={36} radius={4} />
			<div className={styles.skeletonFigureLabel}>
				<Skeleton width={110} height={14} radius={4} />
			</div>
		</div>
		<div className={styles.statusBlock}>
			<Skeleton width={90} height={14} radius={4} />
			<div className={`${styles.statusBar} ${styles.statusBarSkeleton}`} aria-hidden="true" />
			<ul className={styles.statusList}>
				{SEGMENT_KEYS.map((s) => (
					<li key={s.key} className={styles.statusItem}>
						<span className={`${styles.dot} ${styles[`dot_${s.tone}`]} ${styles.dotSkeleton}`} aria-hidden="true" />
						<span className={styles.statusLabel}>
							<Skeleton width={90} height={12} radius={4} />
						</span>
						<span className={styles.statusValue}>
							<Skeleton width={48} height={12} radius={4} />
						</span>
						<span className={styles.statusPct}>
							<Skeleton width={28} height={12} radius={4} />
						</span>
					</li>
				))}
			</ul>
		</div>
	</>
);

/**
 * Inline channel selector for the Reach card — sits at top-right of the card
 * header. Soft outline trigger with chevron, opens a small menu beneath. Built
 * from scratch (no Material-UI DropdownMenu) to match the card aesthetic.
 */
const ChannelDropdown = ({ options, selectedId, onSelect }) => {
	const [open, setOpen] = useState(false);
	const ref = useRef(null);
	const selected = options.find((o) => o.id === selectedId);

	useEffect(() => {
		if (!open) return undefined;
		const handle = (e) => {
			if (ref.current && !ref.current.contains(e.target)) setOpen(false);
		};
		const handleKey = (e) => e.key === 'Escape' && setOpen(false);
		document.addEventListener('mousedown', handle);
		document.addEventListener('keydown', handleKey);
		return () => {
			document.removeEventListener('mousedown', handle);
			document.removeEventListener('keydown', handleKey);
		};
	}, [open]);

	return (
		<div className={styles.channelDropdown} ref={ref}>
			<button
				type="button"
				className={styles.channelTrigger}
				aria-haspopup="listbox"
				aria-expanded={open}
				onClick={() => setOpen((v) => !v)}
			>
				{selected?.iconUrl && <img src={selected.iconUrl} alt="" className={styles.channelIcon} />}
				<span className={styles.channelTriggerLabel}>{selected?.label ?? 'Select channel'}</span>
				<ChevronDownIcon className={styles.channelTriggerChevron} aria-hidden="true" />
			</button>
			{open && (
				<ul className={styles.channelMenu} role="listbox">
					{options.map((opt) => {
						const isSelected = opt.id === selectedId;
						return (
							<li
								key={opt.id}
								role="option"
								aria-selected={isSelected}
								className={`${styles.channelMenuItem} ${isSelected ? styles.channelMenuItemSelected : ''}`}
								onClick={() => {
									onSelect(opt.id);
									setOpen(false);
								}}
							>
								<span className={styles.channelMenuItemLabel}>
									{opt.iconUrl && <img src={opt.iconUrl} alt="" className={styles.channelIcon} />}
									<span>{opt.label}</span>
								</span>
								{isSelected && <CheckIcon className={styles.channelMenuCheck} aria-hidden="true" />}
							</li>
						);
					})}
				</ul>
			)}
		</div>
	);
};
